/**
 * Jeans painting — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_paint_crafts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const jeansPaintingPack: ToilePack = {
    slug: 'jeans-painting',
    category: 'craft',
    name: 'Jeans painting',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'jacket', label: 'Jacket back', subject: 'a denim jacket back painted with a tiger'},
        {key: 'pocket', label: 'Pocket', subject: 'jeans with flowers painted on the back pocket'},
        {key: 'leg', label: 'Leg', subject: 'jeans with a painted snake down one leg'},
    ],
    styles: [
        {key: 'realistic', label: 'Realistic', style: 'detailed acrylic fabric painting with shading and highlights', palette: 'rich natural colors on mid-blue denim', framing: 'hung on a wooden hanger against a white brick wall, seen straight on'},
        {key: 'neon', label: 'Neon pop', style: 'flat graphic fabric paint with thick black outlines', palette: 'fluorescent pink, lime and orange on dark indigo denim', framing: 'laid flat on pale concrete, seen from directly above'},
        {key: 'vintage', label: 'Vintage', style: 'slightly cracked, sun-faded fabric paint like a worn seventies piece', palette: 'faded mustard, rust and cream on light washed denim', framing: 'draped over the back of a wooden chair with the painted side facing out'},
    ],
    directions: ['a dragon', 'sunflowers', "kids' jeans"],
    directionHint: 'try: a dragon',
    seo: {
        title: 'Jeans painting: jacket back, pocket, leg',
        metaDescription: 'Nine denim painting ideas, a tiger on a jacket back, flowers on a pocket and a snake down a leg, in three paint styles.',
        h1: 'Jeans painting ideas: jacket back, pocket, jeans leg',
        tagline: 'Three placements on denim, each in a realistic, a neon pop and a vintage finish.',
        intro: 'These nine show where a design can go on denim: a tiger across the back of a jacket, flowers on a jeans pocket, and a snake painted down one leg. Each placement is done in a realistic style, a neon pop style or a faded vintage style. The pieces are shown hung, laid flat and draped over a chair. Type a subject, such as a dragon, to ask for it.',
        useCases: [
            'a painted denim jacket as a gift',
            'a pocket motif on everyday jeans',
            "a design for kids' jeans",
            'a leg design on a favourite pair',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'A tiger on a jacket back, flowers on a pocket and a snake on a leg, each in a realistic, neon pop or vintage style.'},
            {q: 'Are these photos of real denim?', a: 'No. They are generated pictures of each design, not photographs of a worn garment.'},
            {q: 'What is the difference between the three styles?', a: 'Realistic uses shaded colour on mid-blue denim. Neon pop uses flat fluorescent shapes with thick black outlines. Vintage looks cracked and sun-faded, like an old piece.'},
            {q: 'Can I ask for a different design?', a: "Yes. Type a subject such as a dragon, sunflowers or kids' jeans. A typed subject is a request, not a promised result."},
        ],
    },
}
